import axios from 'axios';
import { getAccessToken } from '../../../keycloak';
import config from '../../../config';


const logger = {
  log: (label: string, data?: any) => console.log(`[JourneyAPI] ${label}`, data || ''),
  error: (label: string, error?: any) => console.error(`[JourneyAPI] ERROR: ${label}`, error || ''),
  warn: (label: string, data?: any) => console.warn(`[JourneyAPI] WARN: ${label}`, data || ''),
};

const createAuthAxios = () => {
  const instance = axios.create({
    baseURL: config.apiServerUrl,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use((config) => {
    const token = getAccessToken();
    const data = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
    logger.log(`Request: ${config.method?.toUpperCase()} ${config.url}`, {
      hasToken: !!token,
      dataType: typeof config.data,
      requesterEmail: data?.requester_email,
      campaignName: data?.campaign_name,
      channelTemplates: data?.channel_templates,
      channelTemplatesType: typeof data?.channel_templates,
      channelTemplatesIsArray: Array.isArray(data?.channel_templates),
      firstTemplate: data?.channel_templates?.[0],
    });
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      logger.log(`Response OK: ${response.status} ${response.config.url}`);
      return response;
    },
    (error) => {
      logger.error(`API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
        status: error.response?.status,
        data: error.response?.data,
      });
      return Promise.reject(error);
    }
  );

  return instance;
};

const authAxios = createAuthAxios();

// No token, no interceptor: the DLT form is opened from an email by an external
// provider who has no Keycloak session. Attaching auth here is what made the
// public form bounce to login.
const publicAxios = axios.create({
  baseURL: config.apiServerUrl,
  headers: { 'Content-Type': 'application/json' },
});

export const journeyRequestsAPI = {
  create: (data: any) =>
    authAxios.post(`/journeys/requests`, data).then((res) => res.data),

  getAll: (params?: any) =>
    authAxios.get(`/journeys/requests`, { params }).then((res) => res.data),

  getById: (id: string) =>
    authAxios.get(`/journeys/requests/${id}`).then((res) => res.data),

  update: (id: string, data: any) =>
    authAxios.patch(`/journeys/requests/${id}`, data).then((res) => res.data),

  addActivity: (id: string, activity: any) =>
    authAxios.post(`/journeys/requests/${id}/activities`, activity).then((res) => res.data),

  getActivities: (id: string) =>
    authAxios.get(`/journeys/requests/${id}/activities`).then((res) => res.data),

  createChannelTemplate: (requestId: string, data: any) =>
    authAxios.post(`/journeys/requests/${requestId}/channel-templates`, data).then((res) => res.data),

  getChannelTemplates: (requestId: string) =>
    authAxios.get(`/journeys/requests/${requestId}/channel-templates`).then((res) => res.data),

  updateChannelTemplate: (requestId: string, templateId: string, data: any) =>
    authAxios.patch(`/journeys/requests/${requestId}/channel-templates/${templateId}`, data).then((res) => res.data),

  deleteChannelTemplate: (requestId: string, templateId: string) =>
    authAxios.delete(`/journeys/requests/${requestId}/channel-templates/${templateId}`).then((res) => res.data),
};

export const gargiDraftAPI = {
  get: (requestId: string) =>
    authAxios.get(`/journeys/requests/${requestId}/gargi-draft`).then((res) => res.data),
  start: (requestId: string, recreate?: boolean) =>
    authAxios.post(`/journeys/requests/${requestId}/gargi-draft`, recreate ? { recreate } : {}).then((res) => res.data),
  markSaved: (requestId: string, journeyId: string) =>
    authAxios.post(`/journeys/requests/${requestId}/gargi-draft/saved`, { journeyId }).then((res) => res.data),
};

export const journeyTemplatesAPI = {
  list: () =>
    authAxios.get(`/journeys/templates`).then((res) => res.data),
};

export const dltFormAPI = {
  getFormStatus: (requestId: string) =>
    authAxios.get(`/journeys/requests/${requestId}/dlt-form`).then((res) => res.data),
  submitForm: (requestId: string, data: any) =>
    authAxios.post(`/journeys/requests/${requestId}/dlt-form`, data).then((res) => res.data),
  approveDltForm: (requestId: string, formId: string, approverComments?: string) =>
    authAxios.post(`/journeys/requests/${requestId}/dlt-form/${formId}/approve`, { approver_comments: approverComments }).then((res) => res.data),
  rejectDltForm: (requestId: string, formId: string, rejectionReason: string) =>
    authAxios.post(`/journeys/requests/${requestId}/dlt-form/${formId}/reject`, { rejection_reason: rejectionReason }).then((res) => res.data),
};

export const dltFormPublicAPI = {
  getForm: (requestId: string) =>
    publicAxios.get(`/journeys/requests/public/dlt-forms/${requestId}`).then((res) => res.data),
  submitForm: (requestId: string, data: any) =>
    publicAxios
      .post(`/journeys/requests/public/dlt-forms/${requestId}/submit`, data)
      .then((res) => res.data),

  // The rule server's storage upload sits behind Keycloak, so the public form
  // uploads through api-server, which resolves the tenant from the request.
  uploadAttachments: (requestId: string, files: Blob[]): Promise<string[]> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return publicAxios
      .post(`/journeys/requests/public/dlt-forms/${requestId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => res.data.urls as string[]);
  },
};

export const getJourneyRequest = (id: string) => journeyRequestsAPI.getById(id);
export const updateJourneyRequest = (id: string, data: any) => journeyRequestsAPI.update(id, data);
export const getRequestActivities = (id: string) => journeyRequestsAPI.getActivities(id);
export const createJourneyRequest = (data: any) => journeyRequestsAPI.create(data);
